/**
 * Authentication API functions for the GDB Cloud Edition.
 *
 * AUTHENTICATION calls go DIRECT to the CLOUD via `authClient` (authUrl) — the
 * sidecar auth proxy has been killed. login/register/forgot-password/refresh
 * hit the cloud's `/api/auth/*` contract (same client the website uses). In the
 * plugin's cloud-SpaSource mode the cloud IS the SPA's origin → same-origin, no
 * CORS.
 *
 * STATUS (getAuthStatus/getMe) also goes to the CLOUD via `authClient` (authUrl).
 * The sidecar is gutted (bridge + local data only) and can no longer be a fresh
 * entitlement source — it would serve a login-time `owned_tools` cache, so a
 * post-login grant (e.g. a purchase) never showed up in the editor. The cloud is
 * the single source of truth for BOTH the session and entitlements; the Bearer
 * the SPA holds in localStorage (set by the login hand-off) authenticates the
 * call, and it resolves same-origin on the website.
 *
 * EXCEPTION: `logout` stays on `api` (the LOCAL sidecar) — it must drop the
 * sidecar's held token, and the cloud has no logout route.
 *
 * After a successful login/register the cloud JWT is handed to the sidecar via
 * `setAuthToken` (see lib/auth/setAuthToken.ts) so the sidecar can make its own
 * authed cloud calls (downloads etc.). JWT persists in localStorage under
 * 'gdb_token'.
 */

import { api, authClient } from '@/lib/api-client'
import type {
  AuthResponse,
  AuthStatus,
  AuthUser,
  ForgotPasswordResult,
  ModuleAccess,
  ResetPasswordResult,
  ValidateResetTokenResult,
} from './types'

const TOKEN_KEY = 'gdb_token'

/**
 * Login with email and password.
 *
 * `rememberMe` controls whether the sidecar persists the session across
 * restarts via the OS credential store (issue #81). Defaults to true — the
 * typical user wants to stay signed in — and matches the checkbox default in
 * AuthCard. Pass false to opt out; the sidecar will hold the session in
 * memory only and sign the user out on the next restart.
 *
 * POST /api/auth/login
 */
export async function login(
  email: string,
  password: string,
  rememberMe: boolean = true
): Promise<AuthResponse<AuthStatus>> {
  const response = await authClient.post('/api/auth/login', {
    email,
    password,
    rememberMe,
  })
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    return { success: false, error: data.error || 'Login failed' }
  }
  return response.json() as Promise<AuthResponse<AuthStatus>>
}

/**
 * Register a new account. Returns same shape as login (auto-login on register).
 * POST /api/auth/register
 */
export async function register(
  name: string,
  email: string,
  password: string
): Promise<AuthResponse<AuthStatus>> {
  const response = await authClient.post('/api/auth/register', { name, email, password })
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    return { success: false, error: data.error || 'Registration failed' }
  }
  return response.json() as Promise<AuthResponse<AuthStatus>>
}

/**
 * Request a password reset email.
 * POST /api/auth/forgot-password
 */
export async function forgotPassword(
  email: string
): Promise<AuthResponse<ForgotPasswordResult>> {
  const response = await authClient.post('/api/auth/forgot-password', { email })
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    return { success: false, error: data.error || 'Request failed' }
  }
  return response.json() as Promise<AuthResponse<ForgotPasswordResult>>
}

/**
 * Validate a password-reset token before showing the reset form.
 *
 * GET /api/auth/validate-reset-token?token=XXX → 200 { valid: true } for a
 * live token, 400 { valid: false, error } for a missing/expired one. Goes
 * DIRECT to the cloud via `authClient` (same as forgotPassword) — the reset
 * link lands on the cloud-served SPA, and the token lives in the cloud DB.
 *
 * A non-200 degrades to { success: false, error } so the caller can treat
 * either signal (HTTP non-ok OR `valid:false`) as a dead link.
 */
export async function validateResetToken(
  token: string
): Promise<AuthResponse<ValidateResetTokenResult>> {
  const response = await authClient.get(
    `/api/auth/validate-reset-token?token=${encodeURIComponent(token)}`
  )
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    return { success: false, error: data.error || 'Invalid or expired token' }
  }
  const data = (await response.json()) as ValidateResetTokenResult
  // The backend uses 200 only for valid tokens, but guard on the body too in
  // case that ever changes — a `valid:false` 200 is still a dead link.
  if (!data.valid) {
    return { success: false, error: data.error || 'Invalid or expired token' }
  }
  return { success: true, data }
}

/**
 * Complete a password reset: set a new password using a valid reset token.
 *
 * POST /api/auth/reset-password { token, newPassword } → success JSON, or 400
 * "Invalid or expired reset token" / "Password must be 8-256 characters". On
 * success the backend consumes the token and updates the user's password.
 * Goes DIRECT to the cloud via `authClient`, same pattern as forgotPassword.
 *
 * NOTE: the wire field is `newPassword` (camelCase) — the Rust
 * `ResetPasswordRequest` is `#[serde(rename_all = "camelCase")]`.
 */
export async function resetPassword(
  token: string,
  newPassword: string
): Promise<AuthResponse<ResetPasswordResult>> {
  const response = await authClient.post('/api/auth/reset-password', {
    token,
    newPassword,
  })
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    return { success: false, error: data.error || 'Password reset failed' }
  }
  const data = (await response.json().catch(() => ({}))) as ResetPasswordResult
  return { success: true, data: { ...data, success: true } }
}

/**
 * Refresh auth session using the stored JWT token.
 * POST /api/auth/refresh with Authorization: Bearer <token>
 */
export async function refreshAuth(
  token: string
): Promise<AuthResponse<AuthStatus>> {
  const response = await authClient.post('/api/auth/refresh', undefined, {
    Authorization: `Bearer ${token}`,
  })
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    return { success: false, error: data.error || 'Refresh failed' }
  }
  return response.json() as Promise<AuthResponse<AuthStatus>>
}

/**
 * Fetch the current auth status from the sidecar's session.
 *
 * GET /api/auth/status returns the full AuthStatus (loggedIn, valid,
 * user, modules, …) based on whatever session the sidecar has on disk.
 * This is the canonical "am I signed in" query — the sidecar's session
 * is the single source of truth. Don't gate this call on localStorage:
 * the localStorage token is just a cache for the api-client's Bearer
 * header and can drift from the sidecar's session (server-side logout,
 * sidecar rebuild, expired session). Asking the sidecar each time keeps
 * React's view honest.
 */
export async function getAuthStatus(): Promise<AuthResponse<AuthStatus>> {
  const response = await authClient.get('/api/auth/status')
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    return { success: false, error: data.error || 'Failed to get auth status' }
  }
  return response.json() as Promise<AuthResponse<AuthStatus>>
}

/**
 * Get current user info using the stored JWT token.
 * GET /api/auth/status with Authorization: Bearer <token>
 *
 * Note: The server endpoint is /api/auth/status (not /me).
 * Returns the cached session including user and modules.
 */
export async function getMe(
  token: string
): Promise<AuthResponse<{ user: AuthUser; modules: ModuleAccess[] }>> {
  const response = await authClient.get('/api/auth/status', {
    Authorization: `Bearer ${token}`,
  })
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    return { success: false, error: data.error || 'Failed to get user info' }
  }
  return response.json() as Promise<
    AuthResponse<{ user: AuthUser; modules: ModuleAccess[] }>
  >
}

/**
 * Logout: clear the sidecar session AND the local token cache.
 *
 * The sidecar holds the canonical session in app_settings — clearing
 * just localStorage left the sidecar still "logged in" so the next
 * status fetch returned loggedIn:true and the AuthCard never showed.
 * POST /api/auth/logout drops the server-side session; the localStorage
 * removal is best-effort cleanup for the api-client's Bearer cache.
 */
export async function logout(): Promise<{ success: true }> {
  // Best-effort: even if the request fails (sidecar offline) we still
  // drop the local token so the UI can react. The next /api/auth/status
  // call will re-confirm whatever the sidecar actually thinks.
  await api.post('/api/auth/logout').catch(() => undefined)
  localStorage.removeItem(TOKEN_KEY)
  return { success: true }
}
